import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { ShieldCheck, Check, Circle, Share2, Link2Off, Scale, ListChecks, ClipboardCheck, Loader2, Lock, Upload, Eye, AlertTriangle } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { needsUpgrade, openUpgrade } from "@/lib/planFeatures";
import { formatDateTime } from "@/lib/format";
import api from "@/lib/api";

const Bar = ({ pct, color = "bg-brand" }) => (
  <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
    <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
  </div>
);

function StatCard({ icon: Icon, label, value, sub, pct, color, testid }) {
  return (
    <Card className="rounded-xl border p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
      <Icon className="h-4 w-4 text-brand-ink" />
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 flex items-end gap-2">
        <span className="h-display text-3xl font-bold text-foreground">{value}</span>
        {sub && <span className="mb-1 text-xs text-muted-foreground">{sub}</span>}
      </div>
      {pct != null && <div className="mt-3"><Bar pct={pct} color={color} /></div>}
    </Card>
  );
}

const legalColor = (band) => band === "Strong" ? "bg-emerald-500" : band === "Getting there" ? "bg-amber-500" : "bg-red-500";

export default function DiligenceRoom() {
  const { company } = useAuth();
  const [data, setData] = useState(null);
  const [views, setViews] = useState(null);
  const [dropDismissed, setDropDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);
  const pendingItem = useRef(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const locked = company && needsUpgrade(company.plan, "growth");

  const load = useCallback(async () => {
    if (!company) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${company.id}/diligence`, { quiet402: true });
      setData(data);
      if (data.share_token) {
        try { const v = await api.get(`/companies/${company.id}/diligence/views`, { quiet402: true }); setViews(v.data); }
        catch { setViews(null); }
      } else { setViews(null); }
    }
    catch { /* handled by locked state */ }
    finally { setLoading(false); }
  }, [company]);

  useEffect(() => { if (locked) { setLoading(false); } else { load(); } }, [locked, load]);

  const toggle = async (kind, key, value, stage) => {
    try { await api.post(`/companies/${company.id}/diligence/toggle`, { kind, key, value, stage }); load(); }
    catch { toast.error("Could not update"); }
  };
  const setStage = async (stage) => {
    try { await api.post(`/companies/${company.id}/diligence/stage`, { stage }); load(); }
    catch { toast.error("Could not change stage"); }
  };
  const share = async () => {
    try {
      const { data } = await api.post(`/companies/${company.id}/diligence/share`);
      const url = `${window.location.origin}/dd/${data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Investor DD link copied to clipboard"); }
      catch { toast.success(`DD link: ${url}`); }
      load();
    } catch { toast.error("Could not create link"); }
  };
  const unshare = async () => {
    try { await api.delete(`/companies/${company.id}/diligence/share`); toast.success("DD link revoked"); load(); }
    catch { toast.error("Could not revoke link"); }
  };

  const pickFile = (item) => { pendingItem.current = item; fileRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const item = pendingItem.current;
    pendingItem.current = null;
    if (!file || !item) return;
    setUploadingKey(item.key);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", item.upload_category);
    fd.append("visibility", "founder_only");
    try {
      await api.post(`/companies/${company.id}/documents`, fd);
      toast.success(`Uploaded to ${item.upload_category} - "${item.label}" is now covered`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingKey(null); }
  };

  if (locked) {
    return (
      <AppShell title="Diligence Room" subtitle="Investor-ready DD index, legal health & milestones">
        <Card className="mx-auto mt-10 max-w-lg rounded-xl border p-8 text-center" data-testid="diligence-upgrade">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary"><Lock className="h-6 w-6 text-primary-foreground" /></div>
          <h2 className="ff-cabinet mt-4 text-xl font-bold text-foreground">Get investor-ready, faster</h2>
          <p className="mt-2 text-sm text-muted-foreground">The Diligence Room maps a standard due-diligence index to what's already in your data room, scores your legal health, and tracks your milestones to each round. Available on Growth and above.</p>
          <Button className="mt-5" onClick={() => openUpgrade({ plan: "growth" })} data-testid="diligence-upgrade-btn">Upgrade to unlock</Button>
        </Card>
      </AppShell>
    );
  }

  const shareActions = data?.share_token ? (
    <div className="flex items-center gap-1.5">
      <Button variant="outline" size="sm" className="gap-1.5" onClick={share} data-testid="dd-copy-link"><Share2 className="h-3.5 w-3.5" /> Copy DD link</Button>
      <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-red-600" onClick={unshare} data-testid="dd-revoke"><Link2Off className="h-3.5 w-3.5" /> Revoke</Button>
    </div>
  ) : (
    <Button variant="outline" size="sm" className="gap-1.5" onClick={share} data-testid="dd-share"><Share2 className="h-3.5 w-3.5" /> Share DD index</Button>
  );

  return (
    <AppShell title="Diligence Room" subtitle="Investor-ready DD index, legal health & milestones" actions={data ? shareActions : null}>
      {loading || !data ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-6">
          <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="dd-file-input" />

          {data.readiness_drop && !dropDismissed && (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="readiness-drop-banner">
              <div className="flex items-center gap-2 text-sm text-amber-800"><AlertTriangle className="h-4 w-4 shrink-0" /> Your diligence readiness dropped from {data.readiness_drop.from}% to {data.readiness_drop.to}% - a document may have been removed or unshared.</div>
              <button onClick={() => setDropDismissed(true)} className="shrink-0 text-xs font-medium text-amber-700 hover:underline" data-testid="drop-dismiss">Dismiss</button>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard icon={ClipboardCheck} label="Diligence ready" value={`${data.dd.pct}%`} sub={`${data.dd.ready}/${data.dd.total} items`} pct={data.dd.pct} testid="stat-dd" />
            <StatCard icon={Scale} label="Legal health" value={data.legal.score} sub={`/ 100 · ${data.legal.band}`} pct={data.legal.score} color={legalColor(data.legal.band)} testid="stat-legal" />
            <StatCard icon={ListChecks} label={`${data.milestones.label} milestones`} value={`${data.milestones.pct}%`} sub={`${data.milestones.done}/${data.milestones.total} done`} pct={data.milestones.pct} testid="stat-milestones" />
          </div>

          {data.share_token && views && views.count > 0 && (
            <Card className="rounded-xl border p-5" data-testid="dd-views-card">
              <div className="flex items-center gap-2"><Eye className="h-4 w-4 text-brand-ink" /><h3 className="ff-cabinet text-sm font-bold text-foreground">Who's viewing your DD index</h3></div>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                <span className="text-foreground"><b className="h-display">{views.count}</b> opens</span>
                <span className="text-foreground"><b className="h-display">{views.unique}</b> unique viewers</span>
                {views.last_viewed_at && <span className="text-muted-foreground">Last opened {formatDateTime(views.last_viewed_at)}</span>}
              </div>
              <div className="mt-3 space-y-1 border-t pt-3">
                {views.recent.map((v, i) => (
                  <div key={i} className="flex justify-between text-xs text-muted-foreground" data-testid="dd-view-row"><span>{v.location || "Unknown location"}</span><span>{formatDateTime(v.viewed_at)}</span></div>
                ))}
              </div>
            </Card>
          )}

          <Tabs defaultValue="dd">
            <TabsList data-testid="diligence-tabs">
              <TabsTrigger value="dd" data-testid="tab-dd"><ClipboardCheck className="mr-1.5 h-4 w-4" /> Diligence Index</TabsTrigger>
              <TabsTrigger value="legal" data-testid="tab-legal"><Scale className="mr-1.5 h-4 w-4" /> Legal Health</TabsTrigger>
              <TabsTrigger value="milestones" data-testid="tab-milestones"><ListChecks className="mr-1.5 h-4 w-4" /> Milestones</TabsTrigger>
            </TabsList>

            {/* Diligence index */}
            <TabsContent value="dd" className="mt-5 space-y-4">
              <p className="text-sm text-muted-foreground">A standard investor diligence index, auto-matched to what's already in your data room and cap table. Tick anything you keep elsewhere, then share a read-only index with investors.</p>
              {data.dd.categories.map((cat) => (
                <Card key={cat.category} className="rounded-xl border p-5" data-testid={`dd-cat-${cat.category.replace(/\W+/g, "-").toLowerCase()}`}>
                  <div className="mb-1 flex items-center justify-between">
                    <h3 className="ff-cabinet text-sm font-bold text-foreground">{cat.category}</h3>
                    <span className="text-xs text-muted-foreground">{cat.items.filter((i) => i.ready).length}/{cat.items.length}</span>
                  </div>
                  <div className="divide-y">
                    {cat.items.map((it) => (
                      <div key={it.key} className="flex items-center justify-between gap-3 py-2.5" data-testid={`dd-item-${it.key}`}>
                        <div className="flex items-start gap-2.5">
                          {it.ready ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2.4} /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />}
                          <div>
                            <div className="text-sm font-medium text-foreground">{it.label}</div>
                            <div className="text-xs text-muted-foreground">
                              {it.source === "auto" ? (it.doc_count > 0 ? `${it.doc_count} document${it.doc_count > 1 ? "s" : ""} in your data room` : "Detected in TenX") : it.manual ? "Marked as ready" : "Not found - upload a doc or mark it ready"}
                            </div>
                          </div>
                        </div>
                        {it.source !== "auto" && (
                          <div className="flex shrink-0 items-center gap-2">
                            {!it.ready && it.upload_category && (
                              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => pickFile(it)} disabled={uploadingKey === it.key} data-testid={`dd-upload-${it.key}`}>
                                {uploadingKey === it.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
                              </Button>
                            )}
                            <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-muted-foreground">
                              <Checkbox checked={it.manual} onCheckedChange={(v) => toggle("dd", it.key, !!v)} data-testid={`dd-toggle-${it.key}`} /> We have this
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </TabsContent>

            {/* Legal health */}
            <TabsContent value="legal" className="mt-5 space-y-3">
              <Card className="rounded-xl border p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="ff-cabinet text-lg font-bold text-foreground">Legal health {data.legal.score}/100</div>
                    <div className="text-sm text-muted-foreground">Startup legal-hygiene basics. Not legal advice - a checklist to keep you clean and investor-ready.</div>
                  </div>
                  <Badge className={`${data.legal.band === "Strong" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : data.legal.band === "Getting there" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-red-200 bg-red-50 text-red-700"}`} data-testid="legal-band">{data.legal.band}</Badge>
                </div>
                <div className="mt-3"><Bar pct={data.legal.score} color={legalColor(data.legal.band)} /></div>
              </Card>
              <div className="space-y-2">
                {data.legal.items.map((it) => (
                  <Card key={it.key} className="flex items-center justify-between gap-3 rounded-xl border p-4" data-testid={`legal-item-${it.key}`}>
                    <div className="flex items-start gap-2.5">
                      {it.done ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2.4} /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />}
                      <div>
                        <div className="text-sm font-medium text-foreground">{it.label} <span className="text-xs text-muted-foreground">· {it.points} pts</span></div>
                        {!it.done && <div className="text-xs text-muted-foreground">{it.fix}</div>}
                        {it.done && !it.manual && <div className="text-xs text-emerald-600">Detected in TenX</div>}
                      </div>
                    </div>
                    {it.manual ? (
                      <label className="flex shrink-0 cursor-pointer items-center gap-2 text-xs font-medium text-muted-foreground">
                        <Checkbox checked={it.done} onCheckedChange={(v) => toggle("legal", it.key, !!v)} data-testid={`legal-toggle-${it.key}`} /> Done
                      </label>
                    ) : (
                      <Badge variant="outline" className="shrink-0 text-[10px]">auto</Badge>
                    )}
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Milestones */}
            <TabsContent value="milestones" className="mt-5 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">Stage:</span>
                  <Select value={data.stage} onValueChange={setStage}>
                    <SelectTrigger className="h-9 w-44" data-testid="stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">{data.milestones.done}/{data.milestones.total} complete</div>
              </div>
              <Card className="rounded-xl border p-5">
                <Bar pct={data.milestones.pct} />
                <div className="mt-4 divide-y">
                  {data.milestones.tasks.map((t) => (
                    <div key={t.key} className="flex items-center justify-between gap-3 py-2.5" data-testid={`milestone-${t.key}`}>
                      <div className="flex items-center gap-2.5">
                        {t.done ? <Check className="h-4 w-4 text-emerald-600" strokeWidth={2.4} /> : <Circle className="h-4 w-4 text-slate-300" />}
                        <span className="text-sm font-medium text-foreground">{t.label}</span>
                      </div>
                      {t.manual ? (
                        <Checkbox checked={t.done} onCheckedChange={(v) => toggle("milestone", t.key, !!v, data.stage)} data-testid={`milestone-toggle-${t.key}`} />
                      ) : (
                        <Badge variant="outline" className="text-[10px]">auto</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </AppShell>
  );
}
